import { useCallback, useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import InputDecorator from '@enerex/unified-ui-react/form/InputDecorator';
import useShowSnackbar from 'hooks/useShowSnackbar';
import Select from 'components/UnifiedSelect';
import useLookupOptions from '../hooks/useLookupOptions';
import { validateLookupField } from './lookupFormValues';
import { getLookupRecord } from '../api';
import { toLookupOption } from './lookupOption';

const LookupRelatedField = ({ field, control, setValue, disabled }) => {
    const [portalTarget, setPortalTarget] = useState(null);
    // Keep the menu inside the modal's focus boundary, outside its scrolling body.
    const attachContainer = useCallback(element => setPortalTarget(element?.closest('[role="dialog"]') ?? null), []);
    const page = useLookupOptions(field.source);
    const currentLabel = useWatch({ control, name: field.labelField });
    const currentId = useWatch({ control, name: field.name });
    const resolvedOption = page.options.find(option => String(option.value) === String(currentId));
    const [selectionError, setSelectionError] = useState('');
    const showSnackbar = useShowSnackbar();
    const notifiedError = useRef('');
    const errorMessage = page.error || selectionError || '';
    useEffect(() => {
        if (errorMessage && errorMessage !== notifiedError.current) showSnackbar(errorMessage, 'error');
        notifiedError.current = errorMessage;
    }, [errorMessage, showSnackbar]);
    useEffect(() => {
        setSelectionError('');
        if (!field.resolveById || !currentId || currentLabel) return;
        const controller = new AbortController();
        let current = true;
        getLookupRecord(field.source, currentId, controller.signal).then(record => {
            if (current) setValue(field.labelField, toLookupOption(record).label);
        }).catch(() => { if (current) setSelectionError(`Unable to load the saved ${field.label.toLowerCase()} name. Its ID is preserved; you can search for another option.`); });
        return () => { current = false; controller.abort(); };
    }, [field.resolveById, field.source, field.labelField, field.label, currentId, currentLabel, setValue]);
    // Legacy rows may have an ID but no name. Find the label beyond page one,
    // without changing the stored state or treating a missing name as a missing ID.
    useEffect(() => {
        if (currentId && !currentLabel && resolvedOption) setValue(field.labelField, resolvedOption.label);
        if ((field.lockOnEdit || field.resolveFromOptions) && currentId && !currentLabel && !resolvedOption && !page.search && page.hasMore && !page.loading && !page.error) page.loadMore();
    }, [field.lockOnEdit, field.resolveFromOptions, field.labelField, currentId, currentLabel, resolvedOption, page, setValue]);
    return <div ref={attachContainer}>
        <InputDecorator name={field.name} control={control} label={field.label + ' *'}
            rules={{ validate: value => validateLookupField(field, value) }}
            render={({ field: input, fieldState }) => <Select
                className="settings-lookup__form-select" inputId={`lookup-${field.name}`} aria-label={field.label}
                options={page.options} inputValue={page.search} filterOption={null}
                menuPortalTarget={portalTarget} menuPosition="fixed"
                value={page.options.find(option => String(option.value) === String(input.value)) || (input.value ? { value: input.value, label: currentLabel || `Current ${field.label.toLowerCase()}` } : null)}
                onInputChange={(value, action) => { if (action.action === 'input-change') page.setSearch(value.slice(0, 200)); }}
                onChange={option => {
                    if (String(option?.value ?? '') !== String(input.value ?? '')) {
                        field.clearFields?.forEach(name => setValue(name, '', { shouldDirty: true }));
                    }
                    page.setSearch('');
                    input.onChange(option?.value ?? '');
                    setValue(field.labelField, option?.label ?? '', { shouldDirty: true });
                    if (field.stateGuidField) setValue(field.stateGuidField, option?.guid ?? null, { shouldDirty: true });
                }} onBlur={input.onBlur} onMenuScrollToBottom={page.loadMore}
                isDisabled={disabled} isLoading={page.loading} invalid={fieldState.invalid}
                isClearable={false} placeholder={`Select ${field.label.toLowerCase()}`}
                noOptionsMessage={() => page.error ? 'Options could not be loaded.' : page.loading ? 'Loading...' : 'No matching options.'} />} />
    </div>;
};
export default LookupRelatedField;
